import { BILLING_INTERVAL } from "@/data/constants";
import { TBillingInterval } from "@/types/app";
import { TPlanPrice } from "@/types/products";

import getPrecision from "./getPrecision";

export const MONTHS_PER_INTERVAL: Record<TBillingInterval, number> = {
  [BILLING_INTERVAL.MONTHLY]: 1,
  [BILLING_INTERVAL.YEARLY_BILLED_MONTHLY]: 1,
  [BILLING_INTERVAL.HALF_YEARLY]: 6,
  [BILLING_INTERVAL.YEARLY]: 12,
};

const CENTS_PER_UNIT = 100;

export type TIntervalPricing = Pick<
  TPlanPrice,
  | "monthly"
  | "yearly"
  | "halfYearly"
  | "annualMonthly"
  | "discountedMonthly"
  | "discountedYearly"
  | "discountedHalfYearly"
  | "discountedAnnualMonthly"
>;

const BILLING_INTERVALS = Object.keys(
  MONTHS_PER_INTERVAL,
) as TBillingInterval[];

export const isYearlyBilledMonthly = (
  interval: TBillingInterval | null | undefined,
): boolean => interval === BILLING_INTERVAL.YEARLY_BILLED_MONTHLY;

export const parseBillingInterval = (
  value: string | null | undefined,
): TBillingInterval | undefined => {
  const normalized = value?.toLowerCase();
  return BILLING_INTERVALS.find((interval) => interval === normalized);
};

const LIST_PRICE_KEY: Record<TBillingInterval, keyof TIntervalPricing> = {
  [BILLING_INTERVAL.MONTHLY]: "monthly",
  [BILLING_INTERVAL.YEARLY]: "yearly",
  [BILLING_INTERVAL.HALF_YEARLY]: "halfYearly",
  [BILLING_INTERVAL.YEARLY_BILLED_MONTHLY]: "annualMonthly",
};

const DISCOUNTED_PRICE_KEY: Record<TBillingInterval, keyof TIntervalPricing> = {
  [BILLING_INTERVAL.MONTHLY]: "discountedMonthly",
  [BILLING_INTERVAL.YEARLY]: "discountedYearly",
  [BILLING_INTERVAL.HALF_YEARLY]: "discountedHalfYearly",
  [BILLING_INTERVAL.YEARLY_BILLED_MONTHLY]: "discountedAnnualMonthly",
};

// Only a positive finite number counts as a price: 0, null or absent means
// "not offered for this cycle", which has to surface as NA rather than free.
const asPrice = (value: number | null | undefined): number | undefined =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined;

export const hasIntervalPrice = (
  pricing: TIntervalPricing,
  interval: TBillingInterval,
): boolean => asPrice(pricing[LIST_PRICE_KEY[interval]]) !== undefined;

// `undefined`, never 0, when the plan carries no price for the cycle.
export const getIntervalListPrice = (
  pricing: TIntervalPricing,
  interval: TBillingInterval,
): number | undefined => asPrice(pricing[LIST_PRICE_KEY[interval]]);

// The cycle's discount, only when the API actually sent one for that cycle.
export const getIntervalDiscountedPrice = (
  pricing: TIntervalPricing,
  interval: TBillingInterval,
): number | undefined => asPrice(pricing[DISCOUNTED_PRICE_KEY[interval]]);

// The list price to strike through — undefined unless a discount undercuts it.
export const getIntervalStrikePrice = (
  pricing: TIntervalPricing,
  interval: TBillingInterval,
): number | undefined => {
  const discounted = getIntervalDiscountedPrice(pricing, interval);
  if (discounted === undefined) return undefined;

  const list = getIntervalListPrice(pricing, interval);
  return list !== undefined && list > discounted ? list : undefined;
};

// What the cycle charges: its discount when present, otherwise its list price.
export const getIntervalPrice = (
  pricing: TIntervalPricing,
  interval: TBillingInterval,
): number | undefined =>
  getIntervalDiscountedPrice(pricing, interval) ??
  getIntervalListPrice(pricing, interval);

// The cycle's charge, or its list price when discounts are being ignored.
export const resolveIntervalPrice = (
  pricing: TIntervalPricing,
  interval: TBillingInterval,
  useDiscounted: boolean = true,
): number | undefined =>
  useDiscounted
    ? getIntervalPrice(pricing, interval)
    : getIntervalListPrice(pricing, interval);

// Rounded up to the cent, so a yearly or half-yearly charge never advertises a
// `/m` rate that multiplies back to less than the user is billed.
const spreadPrice = (
  charge: number,
  interval: TBillingInterval,
  per: TBillingInterval,
): number => {
  // No conversion, no rounding: the cycle's own price shows its exact cents.
  if (interval === per) return charge;

  const spread =
    (charge * MONTHS_PER_INTERVAL[per]) / MONTHS_PER_INTERVAL[interval];
  // getPrecision strips float noise first, so 7.5 * 100 can't ceil to 751
  return Math.ceil(getPrecision(spread * CENTS_PER_UNIT, 6)) / CENTS_PER_UNIT;
};

// The cycle's charge spread over another cycle — a `/m` headline is `per: "monthly"`.
export const getPricePerInterval = (
  pricing: TIntervalPricing,
  interval: TBillingInterval,
  per: TBillingInterval,
): number | undefined => {
  const charge = getIntervalPrice(pricing, interval);
  if (charge === undefined) return undefined;

  return spreadPrice(charge, interval, per);
};

// The pre-discount rate to strike through — undefined unless the cycle is discounted.
export const getListPricePerInterval = (
  pricing: TIntervalPricing,
  interval: TBillingInterval,
  per: TBillingInterval,
): number | undefined => {
  if (getIntervalDiscountedPrice(pricing, interval) === undefined) {
    return undefined;
  }

  const list = getIntervalListPrice(pricing, interval);
  if (list === undefined) return undefined;

  return spreadPrice(list, interval, per);
};

// Sums prices that must all be present — `undefined` if any cycle is unpriced.
export const sumIntervalPrices = (
  amounts: (number | undefined)[],
): number | undefined =>
  amounts.reduce<number | undefined>(
    (total, amount) =>
      total === undefined || amount === undefined ? undefined : total + amount,
    0,
  );
